import { ChatNotificationListener } from "@qw/chat";
import { useTabScopedStorage } from "@qw/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import { useInboxQueryStates } from "./inbox-query-states-provider";
import { scopedInsuredId, scopeSegment, useInboxScope } from "./inbox-scope";
import { PageContext } from "./use-page";

export enum ChatPosition {
  /** Persisted as `"Floating"`, which predates the dock it now opens as. */
  dock = "Floating",
  side = "Sidebar",
}

export interface PageContextValue {
  canChat: boolean;
  chatPanel: React.ReactNode | null;
  chatMounted: boolean;
  chatPosition: ChatPosition;
  isBookPinnedInRail: boolean;
  isChatOpen: boolean;
  isLeftNavCollapsed: boolean;
  isMiniAppOpen: boolean;
  isRightNavCollapsed: boolean;
  pinBookInRail: () => void;
  unpinBookInRail: () => void;
  rootPath: string;
  /**
   * The inbox root plus whatever the route is scoped to, and `rootPath` when it is scoped to
   * nothing. Every navigation that means "stay where I am, change something else" — a folder
   * switch, a committed search, an email pick that has to travel — targets this rather than
   * `rootPath`, which is what keeps the scope chip up across all of them.
   */
  rootScopePath: string;
  setChatPanel: React.Dispatch<React.SetStateAction<React.ReactNode | null>>;
  setChatMounted: React.Dispatch<React.SetStateAction<boolean>>;
  setChatPosition: React.Dispatch<React.SetStateAction<ChatPosition>>;
  setIsChatOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLeftNavCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  setIsMiniAppOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRightNavCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

export const PageProvider = ({ children, rootPath }: { children: React.ReactNode; rootPath: string }) => {
  const { conversationId, openChat, clearOpenChat } = useInboxQueryStates();
  const { pathname } = useLocation();
  const scope = useInboxScope();
  const [chatPanel, setChatPanel] = useState<React.ReactNode | null>(null);
  const [chatMounted, setChatMounted] = useState(false);
  // Sidebar-vs-floating is a genuine cross-tab preference, so it stays in
  // localStorage. Whether a panel is open right now is per-tab: see
  // useTabScopedStorage for why sharing that across tabs was a bug (B-1703).
  const [chatPosition, setChatPosition] = useLocalStorage("qwibChatPosition", ChatPosition.side);
  const [isChatOpen, setIsChatOpen] = useTabScopedStorage("qwibIsChatOpen", false);
  const [isLeftNavCollapsed, setIsLeftNavCollapsed] = useTabScopedStorage("qwibIsLeftNavCollapsed", false);
  const [isMiniAppOpen, setIsMiniAppOpen] = useTabScopedStorage("isMiniAppOpen", true);
  const [isRightNavCollapsed, setIsRightNavCollapsed] = useTabScopedStorage("qwibIsRightNavCollapsed", true);
  const [dismissedRouteInsuredId, setDismissedRouteInsuredId] = useState<string | null>(null);

  const routeInsuredId = scopedInsuredId(scope);
  const canChat = Boolean(chatPanel || conversationId || routeInsuredId);
  const isRootInsuredRoute = Boolean(
    routeInsuredId &&
      (pathname === `${rootPath}/${routeInsuredId}` || pathname.startsWith(`${rootPath}/${routeInsuredId}/`))
  );
  const routeForcesMiniAppOpen = isRootInsuredRoute && dismissedRouteInsuredId !== routeInsuredId;
  const effectiveIsMiniAppOpen = routeForcesMiniAppOpen || isMiniAppOpen;
  const setEffectiveIsMiniAppOpen = useCallback<React.Dispatch<React.SetStateAction<boolean>>>(
    (nextValue) => {
      const nextResolvedValue = typeof nextValue === "function" ? nextValue(effectiveIsMiniAppOpen) : nextValue;

      setDismissedRouteInsuredId(isRootInsuredRoute && !nextResolvedValue ? (routeInsuredId ?? null) : null);
      setIsMiniAppOpen(nextResolvedValue);
    },
    [effectiveIsMiniAppOpen, isRootInsuredRoute, routeInsuredId, setIsMiniAppOpen]
  );

  // "Back to book" in the merged rail, where the Book and the insured share one
  // slot. It cannot clear the selection — the broker's open email has to stay
  // open — so it needs an override that outranks the insured.
  //
  // Keyed to the selection it was made in rather than a plain boolean, because
  // the Book has to stay a work queue: the next email or insured the broker
  // picks hands the rail back on its own, so the pin cannot outlive the
  // selection that asked for it. Comparing keys during render, like
  // `dismissedRouteInsuredId` above, means the pin is gone in the same render the
  // selection moved rather than a frame later.
  //
  // The key covers every way out except the one the broker is most likely to
  // want: staying on the email they pinned from and asking for its insured back.
  // `unpinBookInRail` is that way out, behind the Book header's own control.
  //
  // The route is part of the key because this provider is a singleton above
  // `/inbox` and `/drafts` both, and `FolderNav` carries the insured across
  // (`/drafts/:insuredId`) — so a Drafts round trip leaves the selection
  // untouched and would hand back a pin made before the broker left the rail.
  // Folding it in here is what makes the effect below forget that pin, on the
  // same rule a new selection uses.
  const [bookPinnedForSelection, setBookPinnedForSelection] = useState<string | null>(null);
  const isRailPath = pathname === rootPath || pathname.startsWith(`${rootPath}/`);
  const railSelectionKey = `${isRailPath}|${conversationId ?? ""}|${routeInsuredId ?? ""}`;
  const isBookPinnedInRail = bookPinnedForSelection === railSelectionKey;
  const pinBookInRail = useCallback(() => setBookPinnedForSelection(railSelectionKey), [railSelectionKey]);
  const unpinBookInRail = useCallback(() => setBookPinnedForSelection(null), []);

  useEffect(() => {
    if (!isRootInsuredRoute) {
      setDismissedRouteInsuredId(null);
    }
  }, [isRootInsuredRoute]);

  // Left behind, a stale key would match again when the broker reopened that
  // email — pinning the Book from a click that asked for the insured.
  useEffect(() => {
    setBookPinnedForSelection((pinnedSelection) => (pinnedSelection === railSelectionKey ? pinnedSelection : null));
  }, [railSelectionKey]);

  // Auto-open chat when navigating from a notification
  useEffect(() => {
    if (openChat) {
      setIsChatOpen(true);
      clearOpenChat();
    }
  }, [openChat, setIsChatOpen, clearOpenChat]);

  const value = {
    canChat,
    chatPanel,
    chatMounted,
    chatPosition,
    isBookPinnedInRail,
    isChatOpen,
    isLeftNavCollapsed,
    isMiniAppOpen: effectiveIsMiniAppOpen,
    isRightNavCollapsed,
    pinBookInRail,
    unpinBookInRail,
    rootPath,
    rootScopePath: scope ? `${rootPath}/${scopeSegment(scope)}` : rootPath,
    setChatPanel,
    setChatMounted,
    setChatPosition,
    setIsChatOpen,
    setIsLeftNavCollapsed,
    setIsMiniAppOpen: setEffectiveIsMiniAppOpen,
    setIsRightNavCollapsed,
  };

  return (
    <PageContext.Provider value={value}>
      <ChatNotificationListener
        alertFaviconHref="/images/inbox-alert.svg"
        onNotificationClick={({ inboxConversationId, insuredId: notificationInsuredId }) => {
          const params = new URLSearchParams({ openChat: "true" });
          let nextPath = rootPath;

          if (inboxConversationId) {
            params.set("conversationId", inboxConversationId);
          } else if (notificationInsuredId) {
            nextPath = `${rootPath}/${notificationInsuredId}`;
          }

          globalThis.location.href = `${globalThis.location.origin}${nextPath}?${params.toString()}`;
        }}
      />
      {children}
    </PageContext.Provider>
  );
};
